/**
 * Canonical field order + zone metadata for the Deals card field grid.
 *
 * Phase 1 of the deals-card-fields buildout — see
 * docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §2.
 *
 * The grid renderer iterates DEALS_FIELD_ORDER (NOT payload.fields)
 * so two cards with different aggregator outputs read consistently.
 * Fields present in the payload but absent from this list log a
 * console warning and render at the end of the grid — graceful
 * forward-compat for new aggregator field names.
 *
 * FIELD_CONTROLS will land in Phase 2 (ENUM dropdowns); for Phase 1
 * we only need ORDER + ZONES.
 */

export type FieldZone =
  | 'identity'
  | 'team'
  | 'classification'
  | 'people'
  | 'financials'
  | 'narrative';

/** Whether the field renders full-width or as a half-width tile in the
 *  2-column grid. The CSS grid is auto-flow row; half-width tiles pair
 *  up automatically as long as the order alternates correctly. */
export type FieldWidth = 'full' | 'half';

export interface FieldDef {
  name: string;
  zone: FieldZone;
  width: FieldWidth;
  /** Human-readable label rendered in the card instead of the raw backend
   *  field name (field-UX-refinements spec §7). */
  label: string;
}

/**
 * The canonical order. Mirrors the Project Kaizen screenshot zone by zone:
 *   Identity  → company / codename
 *   Team      → ember deal team
 *   Classification → stage / type / vertical / themes / sourcing × 2
 *   People    → advisor firm / counterparty contacts
 *   Financials → EV / est_investment / revenue / EBITDA
 *   Narrative → opportunity_description / next_steps
 */
export const DEALS_FIELD_DEFS: readonly FieldDef[] = [
  { name: 'company_name',            zone: 'identity',       width: 'full', label: 'Company' },
  { name: 'project_codename',        zone: 'identity',       width: 'full', label: 'Project Codename' },
  { name: 'deal_team_emails',        zone: 'team',           width: 'full', label: 'Deal Team' },
  { name: 'proposed_stage',          zone: 'classification', width: 'half', label: 'Stage' },
  { name: 'deal_type',               zone: 'classification', width: 'half', label: 'Deal Type' },
  { name: 'vertical',                zone: 'classification', width: 'half', label: 'Vertical' },
  { name: 'themes',                  zone: 'classification', width: 'half', label: 'Themes' },
  { name: 'sourcing_type',           zone: 'classification', width: 'half', label: 'Sourcing Type' },
  { name: 'sourcing_channel',        zone: 'classification', width: 'half', label: 'Sourcing Channel' },
  { name: 'advisor_firm',            zone: 'people',         width: 'half', label: 'Advisor Firm' },
  { name: 'counterparty_contacts',   zone: 'people',         width: 'half', label: 'Counterparty Contacts' },
  { name: 'ev_estimate_mm',          zone: 'financials',     width: 'half', label: 'EV ($M)' },
  { name: 'est_investment_mm',       zone: 'financials',     width: 'half', label: 'Est. Investment ($M)' },
  { name: 'revenue_mm',              zone: 'financials',     width: 'half', label: 'Revenue ($M)' },
  { name: 'ebitda_mm',               zone: 'financials',     width: 'half', label: 'EBITDA ($M)' },
  { name: 'opportunity_description', zone: 'narrative',      width: 'full', label: 'Opportunity Description' },
  { name: 'next_steps',              zone: 'narrative',      width: 'full', label: 'Next Steps' },
] as const;

/** Names-only convenience export. */
export const DEALS_FIELD_ORDER: readonly string[] = DEALS_FIELD_DEFS.map(
  (d) => d.name,
);

const _KNOWN_NAMES = new Set(DEALS_FIELD_ORDER);

export function isKnownField(name: string): boolean {
  return _KNOWN_NAMES.has(name);
}

export function getFieldDef(name: string): FieldDef | undefined {
  return DEALS_FIELD_DEFS.find((d) => d.name === name);
}

/** Human-readable label for a field; falls back to a title-cased version of
 *  the raw backend name for forward-compat with unknown fields. */
export function getFieldLabel(name: string): string {
  const def = DEALS_FIELD_DEFS.find((d) => d.name === name);
  if (def) return def.label;
  return name
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}


/* ------------------------------------------------------------------ *
 * Phase 2 — FIELD_CONTROLS map drives which input renders per field. *
 *                                                                    *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §7.3.   *
 *                                                                    *
 * Phase 2 wires the dropdown types (single + multi). Phase 3b adds   *
 * `team_chip` and `contact_rows` for the structured editors. The     *
 * default for unmapped fields is `text` so the existing free-text    *
 * editor stays the fallback.                                         *
 * ------------------------------------------------------------------ */

export type FieldControlType =
  | 'text'
  | 'textarea'
  | 'numeric'
  | 'dropdown_single'
  | 'dropdown_multi'
  | 'team_chip'        // Phase 3b
  | 'contact_rows';    // Phase 3b

export const FIELD_CONTROLS: Record<string, FieldControlType> = {
  proposed_stage:          'dropdown_single',
  deal_type:               'dropdown_single',
  vertical:                'dropdown_single',
  themes:                  'dropdown_multi',
  sourcing_type:           'dropdown_single',
  sourcing_channel:        'dropdown_single',
  deal_team_emails:        'team_chip',
  counterparty_contacts:   'contact_rows',
  ev_estimate_mm:          'numeric',
  est_investment_mm:       'numeric',
  revenue_mm:              'numeric',
  ebitda_mm:               'numeric',
  opportunity_description: 'textarea',
  next_steps:              'textarea',
  // implicit default for unlisted fields: 'text'
};

export function getFieldControl(name: string): FieldControlType {
  return FIELD_CONTROLS[name] ?? 'text';
}
